import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, Assignment, Assessment, Timeline as TimelineIcon, Warning, Tune } from '@mui/icons-material';
import api from '../lib/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import FidelityDashboard from '../components/FidelityDashboard';
import BarrierTracker from '../components/BarrierTracker';
import AdaptationTracker from '../components/AdaptationTracker';

const TimelinePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = React.useState(0);

  // Fetch timeline data
  const { data: timeline, isLoading } = useQuery({
    queryKey: ['timeline', id],
    queryFn: async () => {
      const res = await api.get(`/agreements/${id}/timeline`);
      return res.data.data;
    },
    enabled: !!id
  });

  // Update task mutation
  const updateTaskMutation = useMutation({
    mutationFn: async ({ taskId, updates }: { taskId: string; updates: any }) => {
      const res = await api.patch(`/tasks/${taskId}`, updates);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeline', id] });
      queryClient.invalidateQueries({ queryKey: ['fidelity', id] });
    }
  });

  const handleTaskToggle = (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'COMPLETED' ? 'PENDING' : 'COMPLETED';
    updateTaskMutation.mutate({
      taskId,
      updates: { status: newStatus }
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'IN_PROGRESS':
        return 'warning';
      case 'PENDING':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    return status === 'COMPLETED' ? (
      <CheckCircle color="success" />
    ) : (
      <RadioButtonUnchecked />
    );
  };

  if (isLoading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!timeline || !timeline.milestones || timeline.milestones.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box mt={4}>
          <Typography variant="h4" gutterBottom>
            Timeline & Progress
          </Typography>
          <Alert severity="info">
            No timeline data available. Tasks will be generated once the agreement is executed.
          </Alert>
        </Box>
      </Container>
    );
  }

  // Calculate overall progress
  const allTasks = timeline.milestones.flatMap((m: any) => m.tasks);
  const completedTasks = allTasks.filter((t: any) => t.status === 'COMPLETED').length;
  const totalTasks = allTasks.length;
  const progressPercent = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  return (
    <Container maxWidth="lg">
      <Box mt={4} mb={4}>
        <Typography variant="h4" gutterBottom>
          90-Day Journey Timeline
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Track your progress through the trust launch process
        </Typography>

        {/* Tabs */}
        <Paper elevation={1} sx={{ mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<TimelineIcon />} label="Timeline" iconPosition="start" />
            <Tab icon={<Assessment />} label="Fidelity" iconPosition="start" />
            <Tab icon={<Warning />} label="Barriers" iconPosition="start" />
            <Tab icon={<Tune />} label="Adaptations" iconPosition="start" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {activeTab === 1 && <FidelityDashboard />}
        {activeTab === 2 && <BarrierTracker />}
        {activeTab === 3 && <AdaptationTracker />}

        {activeTab === 0 && (
          <>
        {/* Overall Progress */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Overall Progress</Typography>
            <Typography variant="body1" color="primary">
              {completedTasks} of {totalTasks} tasks completed
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{ height: 10, borderRadius: 5 }}
          />
          <Typography variant="body2" color="text.secondary" mt={1}>
            {Math.round(progressPercent)}% Complete
          </Typography>
        </Paper>

        {/* Timeline */}
        {timeline.milestones.map((milestone: any, milestoneIndex: number) => {
          const milestoneCompleted = milestone.tasks.every((t: any) => t.status === 'COMPLETED');
          const milestoneInProgress = milestone.tasks.some(
            (t: any) => t.status === 'IN_PROGRESS' || t.status === 'COMPLETED'
          );

          return (
            <Paper key={milestone.id} elevation={2} sx={{ p: 3, mb: 4 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    bgcolor: milestoneCompleted
                      ? 'success.main'
                      : milestoneInProgress
                      ? 'warning.main'
                      : 'grey.300',
                    color: milestoneCompleted ? 'white' : 'grey.700'
                  }}
                >
                  {milestoneCompleted ? (
                    <CheckCircle />
                  ) : (
                    <Typography variant="h6">{milestone.month}</Typography>
                  )}
                </Box>
                <Box flex={1}>
                  <Typography variant="h5">
                    Month {milestone.month}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {milestone.title}
                  </Typography>
                </Box>
                <Chip
                  label={
                    milestoneCompleted
                      ? 'Completed'
                      : milestoneInProgress
                      ? 'In Progress'
                      : 'Not Started'
                  }
                  color={getStatusColor(
                    milestoneCompleted ? 'COMPLETED' : milestoneInProgress ? 'IN_PROGRESS' : 'PENDING'
                  )}
                />
              </Box>

              <Grid container spacing={2}>
                {milestone.tasks.map((task: any) => (
                  <Grid item xs={12} md={6} key={task.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 4
                        },
                        borderLeft: task.status === 'COMPLETED' ? '4px solid green' : '4px solid transparent'
                      }}
                      onClick={() => handleTaskToggle(task.id, task.status)}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="start">
                          <Box mr={2} mt={0.5}>
                            {getStatusIcon(task.status)}
                          </Box>
                          <Box flex={1}>
                            <Typography variant="subtitle1" gutterBottom>
                              {task.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {task.description}
                            </Typography>
                            {task.assignee && (
                              <Box display="flex" alignItems="center" mt={1}>
                                <Assignment fontSize="small" sx={{ mr: 0.5 }} />
                                <Typography variant="caption">
                                  {task.assignee.firstName} {task.assignee.lastName}
                                </Typography>
                              </Box>
                            )}
                            <Box mt={1}>
                              <Chip
                                label={task.status}
                                size="small"
                                color={getStatusColor(task.status)}
                                sx={{ mr: 0.5 }}
                              />
                              {task.dueDate && (
                                <Chip
                                  label={`Due: ${format(new Date(task.dueDate), 'MMM dd')}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          );
        })}

        {/* Timeline Legend */}
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Legend
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Chip icon={<CheckCircle />} label="Completed" color="success" size="small" />
            <Chip icon={<RadioButtonUnchecked />} label="Not Started" size="small" />
            <Typography variant="caption" color="text.secondary" alignSelf="center">
              💡 Click on any task to toggle its status
            </Typography>
          </Box>
        </Paper>
          </>
        )}
      </Box>
    </Container>
  );
};

export default TimelinePage;

